import React, { useEffect, useState } from 'react'
import { Drawer } from '../../../../../shared/drawer/drawer'
import { FleetSection, Notice } from '../../../../../shared/sso-workspace/fleet-page'
import { serverMessage } from '../../../../../shared/error/server-message'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'
import { keyErrorMessage } from '../../../../../shared/partner/partner-words'
import { listResources, updateGrants } from '../api'
import { isLive } from '../key-words'
import { grantRows, grantsBody, grantsProblem, levelText, listText, narrowedGroups, startingGrants } from '../grant-words'
import { GrantsGrid } from '../grants-grid'
import '../api-keys.scss'

// A key's permissions per resource (spec 2026-10-20 §10; contract §8), for
// EducationXR staff only (the key's page asks grants-access.js, and renders
// this only then, so nobody else ever reads the resource list): what the
// key can do with each resource content offers here, then any other
// resource its role reaches, in words; "Edit permissions" sets them (PUT
// /api-key/:id/grants). A preset key becomes a custom one, starting from
// what it holds and never wider; the drawer says what that drops. The drawer
// is the create form's frame (owner 2026-10-29): the picker scrolls, and
// Cancel / Save permissions stay pinned at its foot.

// Whether a key's permissions can be set per resource: a live key of the
// organization's own (not bound to a record) with no system key; core
// refuses grants for the others (400 api_key.invalid_grants).
export const grantsEditable = key => !!key && isLive(key) && !key.binding && !key.system_key

// The sentences that name the key's role; when the view doesn't name it they
// say "this key's role" instead.
const roleName = key => (key && key.role && key.role.name) || ''
const usesRoleText = key => (roleName(key) ? `This key uses the ${roleName(key)} role.` : "This key's role sets what it can do.")
const roleLede = key => (roleName(key) ? `What the ${roleName(key)} role lets this key do, resource by resource.` : "What this key's role lets it do, resource by resource.")

// The resources the key holds that a custom key can't be given here (the
// grid offers only the groups content lists): saving sends the grid alone,
// so these go, and the drawer says so before anything is sent. By name.
const droppedGroups = (apiKey, groups) =>
  grantRows(apiKey.grants, groups).filter(row => row.level !== 'none' && !groups.some(group => group.id === row.id)).map(row => row.label)

const GrantsDrawer = ({ apiKey, groups, onClose, onSaved }) => {
  const [grants, setGrants] = useState(() => startingGrants(apiKey.grants, groups))
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const narrowed = apiKey.custom ? [] : narrowedGroups(apiKey.grants, groups)
  const dropped = droppedGroups(apiKey, groups)

  const save = async e => {
    e.preventDefault()
    if (busy) return
    const problem = grantsProblem(grants)
    if (problem) return setError(problem)
    setBusy(true)
    setError(null)
    try {
      const saved = await updateGrants(apiKey.id, grantsBody(grants))
      setBusy(false)
      onSaved(saved)
    } catch (err) {
      setBusy(false)
      setError(keyErrorMessage(err, "The permissions couldn't be saved."), err)
    }
  }

  return (
    <Drawer open title={`Permissions of ${apiKey.name}`} onClose={onClose} size="lg">
      <form className="ak-form" onSubmit={save} noValidate>
        <div className="ak-form-body">
          <p className="sw-hint ak-form-intro">What the key can do with each resource. A key can't be given more than you hold yourself, and Read and write includes deleting.</p>
          {(!apiKey.custom || narrowed.length > 0 || dropped.length > 0) &&
            <div className="ak-form-notices">
              {!apiKey.custom && <Notice tone="warn">{`${usesRoleText(apiKey)} Saving gives it exactly the permissions below instead.`}</Notice>}
              {narrowed.length > 0 &&
                <Notice tone="warn">{`Its role lets it create and change, but not delete, ${listText(narrowed)}. Custom permissions can't say that, so they start at Read here: choose Read and write to also let it create, change and delete them.`}</Notice>}
              {dropped.length > 0 &&
                <Notice tone="warn">
                  {apiKey.custom
                    ? `It also holds ${listText(dropped)}, which can't be set here. Saving removes that access.`
                    : `Its role also reaches ${listText(dropped)}, which custom permissions can't give yet. Saving removes that access.`}
                </Notice>}
            </div>}
          <GrantsGrid groups={groups} value={grants} onChange={setGrants} disabled={busy} />
        </div>
        <div className="ak-form-foot">
          {error &&
            <div className="ak-form-error">
              <p className="text-danger small m-0" role="alert">{error}</p>
              <ErrorReference error={failure} compact />
            </div>}
          <div className="ak-form-actions">
            <button type="button" className="btn sw-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn sw-btn-primary" disabled={busy}>Save permissions</button>
          </div>
        </div>
      </form>
    </Drawer>
  )
}

export const KeyPermissions = ({ apiKey, canEdit, onSaved }) => {
  const [groups, setGroups] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const [editing, setEditing] = useState(false)

  // What can be given here is the organization's, not the key's: read once.
  useEffect(() => {
    let live = true
    listResources().then(
      list => { if (live) setGroups(Array.isArray(list) ? list : []) },
      err => { if (live) { setGroups([]); setError(serverMessage(err, "The resources a key can be given couldn't be loaded."), err) } }
    )
    return () => { live = false }
  }, [setError])

  const known = !!apiKey.grants
  const rows = Array.isArray(groups) && known ? grantRows(apiKey.grants, groups) : []
  const editable = !!canEdit && grantsEditable(apiKey) && Array.isArray(groups) && groups.length > 0

  return (
    <FleetSection id="ak-permissions" title="Permissions"
      lede={apiKey.custom ? 'This key has custom permissions: what it can do with each resource.' : roleLede(apiKey)}
      actions={editable && !editing ? <button type="button" className="btn sw-btn btn-sm" onClick={() => setEditing(true)}>Edit permissions</button> : null}>
      {groups === undefined && !error && <p className="sw-hint m-0">Loading…</p>}
      {error && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} compact />}
      {Array.isArray(groups) && !known && <p className="sw-hint m-0">This key's permissions per resource aren't available yet.</p>}
      {rows.length > 0 &&
        <dl className="sw-dl">
          {rows.map(row => <React.Fragment key={row.id}><dt>{row.label}</dt><dd>{levelText(row.level)}</dd></React.Fragment>)}
        </dl>}
      {editing && <GrantsDrawer apiKey={apiKey} groups={groups} onClose={() => setEditing(false)} onSaved={saved => { setEditing(false); onSaved(saved) }} />}
    </FleetSection>
  )
}
